import api from "services/api";

const authHeaders = () => {
  const token = localStorage.getItem("tk446655440000");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const value = (object, camel, pascal, fallback) =>
  object?.[camel] ?? object?.[pascal] ?? fallback;

const firstPresent = (object, keys, fallback = "") => {
  for (const key of keys) {
    const candidate = object?.[key];
    if (candidate !== null && candidate !== undefined && candidate !== "") return candidate;
  }
  return fallback;
};

const stringValue = (object, keys) => String(firstPresent(object, keys, "") ?? "");

const selectedIds = (rawValue) => {
  const values = Array.isArray(rawValue)
    ? rawValue
    : typeof rawValue === "string"
      ? rawValue.split(",")
      : rawValue == null
        ? []
        : [rawValue];

  return values
    .map((item) => {
      if (item && typeof item === "object") {
        return firstPresent(item, [
          "id", "Id", "provid", "Provid", "dptid", "Dptid",
          "provinceId", "ProvinceId", "departmentId", "DepartmentId",
        ]);
      }
      return item;
    })
    .map((item) => String(item ?? "").trim())
    .filter(Boolean);
};

const normalizedGender = (rawGender) => {
  const gender = String(rawGender ?? "").trim();
  const comparable = gender.toLowerCase();
  if (["male", "homme", "m", "masculin"].includes(comparable)) return "male";
  if (["female", "femme", "f", "feminin", "féminin"].includes(comparable)) return "female";
  return gender;
};

/**
 * Converts the management-list DTO into the exact shape expected by the edit form.
 * The API has returned both camelCase and PascalCase fields across deployments.
 */
export function normalizeUserEditValues(row) {
  const user = row && typeof row === "object" ? row : {};
  let firstName = stringValue(user, ["firstName", "FirstName", "name", "Name"]);
  let lastName = stringValue(user, ["lastName", "LastName", "preName", "PreName"]);

  // Older management responses only exposed FullName. Keep editing usable for
  // those responses while preferring the lossless, separate fields above.
  if (!firstName && !lastName) {
    const fullName = stringValue(user, ["fullName", "FullName"]).trim();
    const separator = fullName.indexOf(" ");
    firstName = separator === -1 ? fullName : fullName.slice(0, separator);
    lastName = separator === -1 ? "" : fullName.slice(separator + 1).trim();
  }

  return {
    firstName,
    lastName,
    title: stringValue(user, ["title", "Title"]),
    gender: normalizedGender(firstPresent(user, ["gender", "Gender"])),
    phone: stringValue(user, ["phone", "Phone", "phoneNumber", "PhoneNumber"]),
    whastappNo: stringValue(user, [
      "whastappNo", "WhastappNo", "whatsappNo", "WhatsappNo", "whatsAppNo", "WhatsAppNo",
    ]),
    Provinces: selectedIds(firstPresent(user, [
      "provinces", "Provinces", "provinceIds", "ProvinceIds",
    ], [])),
    Departments: selectedIds(firstPresent(user, [
      "departments", "Departments", "departmentIds", "DepartmentIds",
    ], [])),
  };
}

export function normalizeManagementResponse(data, pageNumber = 1, pageSize = 25) {
  const items = value(data, "items", "Items", []);
  const pagination = value(data, "pagination", "Pagination", {});
  const summary = value(data, "summary", "Summary", {});

  return {
    items: Array.isArray(items) ? items : [],
    pagination: {
      pageNumber: Number(value(pagination, "pageNumber", "PageNumber", pageNumber)),
      pageSize: Number(value(pagination, "pageSize", "PageSize", pageSize)),
      totalRecords: Number(value(pagination, "totalRecords", "TotalRecords", 0)),
    },
    summary: {
      total: Number(value(summary, "total", "Total", 0)),
      active: Number(value(summary, "active", "Active", 0)),
      inactive: Number(value(summary, "inactive", "Inactive", 0)),
    },
  };
}

export async function getManagedUsers({ status = "all", pageNumber = 1, pageSize = 25, search = "" }) {
  const { data } = await api.get("/Users/management", {
    headers: authHeaders(),
    params: { status, pageNumber, pageSize, search },
  });
  return normalizeManagementResponse(data, pageNumber, pageSize);
}

export async function getManagedUserDetail(userId) {
  const { data } = await api.get("/Users/userinfo", {
    headers: authHeaders(),
    params: { id: userId },
  });

  const payload = data?.data ?? data?.Data ?? data;
  if (!Array.isArray(payload)) return payload && typeof payload === "object" ? payload : null;

  const requestedId = String(userId ?? "");
  return payload.find((item) => String(firstPresent(item, ["userId", "UserId", "id", "Id"])) === requestedId)
    ?? payload[0]
    ?? null;
}

export async function setManagedUserStatus(userId, isActive, reason = "") {
  const { data } = await api.patch(`/Users/${userId}/status`, { isActive, reason }, {
    headers: authHeaders(),
  });
  return data;
}

export async function getManagedRecruiters({ status = "all", pageNumber = 1, pageSize = 25, search = "" }) {
  const { data } = await api.get("/Recruiters/management", {
    headers: authHeaders(),
    params: { status, pageNumber, pageSize, search },
  });
  return normalizeManagementResponse(data, pageNumber, pageSize);
}

export async function setManagedRecruiterStatus(recruiterId, isActive, reason = "") {
  const { data } = await api.patch(`/Recruiters/${recruiterId}/status`, { isActive, reason }, {
    headers: authHeaders(),
  });
  return data;
}

export function accountIsActive(row) {
  const status = row?.isActive ?? row?.IsActive;
  // Legacy list endpoints predate account status and only return active records.
  return status == null ? true : Boolean(status);
}

export function accountCapability(row, capability) {
  const pascal = capability.charAt(0).toUpperCase() + capability.slice(1);
  return Boolean(row?.[capability] ?? row?.[pascal]);
}

export function canChangeAccountStatus(row) {
  const capability = accountIsActive(row) ? "canDeactivate" : "canActivate";
  return accountCapability(row, capability);
}

export function apiErrorMessage(error, fallback) {
  const data = error?.response?.data;
  return data?.message ?? data?.Message ?? data?.error ?? data?.title ?? fallback;
}
